/**
 * Doubling: the player putting one instrument down and picking another up mid-piece.
 *
 * A flute part that takes the piccolo for sixteen bars is one part, on one staff, read by
 * one player. It is not a second part, and making it one gives a score with an empty staff
 * either side of the passage and a page nobody wants to read. So a doubling is written on
 * the bar it happens at, and the part carries on being the part it was.
 *
 * Only what is *played* changes: the name printed over the staff, the sound, and what the
 * instrument reads at. A kit and a tuning stay with the part for the whole piece, because
 * the staff itself is drawn for them -- five lines or one, a stave or a tab -- and a player
 * does not swap a drum kit for a piccolo in the middle of a bar. A preset naming either is
 * refused rather than half-applied: changing what a staff is takes {@link changeInstrument},
 * which rebuilds the part.
 *
 * The notes already written do not move. They are what the player reads, and after a
 * doubling they are read on another instrument and so sound somewhere else -- which is the
 * whole point of writing one. Re-spelling a passage for the instrument now in hand is a
 * separate thing a caller can ask for.
 */

import type { MeasureId, PartId } from "../document/ids";
import { instrumentAt, measureIndexOf } from "../document/query";
import type { Instrument, InstrumentChange, Part, PartMeasure, Score } from "../document/types";
import type { Command } from "../commands/types";
import type { InstrumentPreset } from "./instruments";

/** Where a part picks an instrument up, and what it picks up. */
export interface Doubling {
  readonly index: number;
  readonly measureId: MeasureId;
  readonly instrument: InstrumentChange;
}

function partOf(score: Score, id: PartId): Part | null {
  return score.parts.find((candidate) => candidate.id === id) ?? null;
}

/**
 * Hand a part's player another instrument from a bar onwards.
 *
 * Refused at the first bar of the piece: what a part opens on is the part's own instrument,
 * which {@link changeInstrument} changes. Refused for a preset that would make the staff a
 * different kind of staff, since that is a rebuild of the part and not a doubling.
 */
export function takeUpInstrument(
  score: Score,
  partId: PartId,
  measureId: MeasureId,
  preset: InstrumentPreset,
): Command | string {
  const part = partOf(score, partId);
  if (part === null) return "there is no such part";
  if (part.measures[measureId] === undefined) return "that part has nothing for that bar";
  if (measureIndexOf(score, measureId) <= 0) {
    return "a part's first instrument is the part's own; change the part instead";
  }
  if ((preset.percussion ?? null) !== null || part.instrument.percussion !== null) {
    return "a drum part is played on its kit for the whole piece";
  }
  if ((preset.tuning ?? null) !== null || part.instrument.tuning !== null) {
    return "a part written as tablature is tuned for the whole piece";
  }

  return {
    kind: "setInstrument",
    partId,
    measureId,
    instrument: {
      name: preset.name,
      sound: preset.sound,
      midiProgram: preset.midiProgram,
      transposition: preset.transposition,
    },
  };
}

/**
 * Take a doubling off a bar, so the player carries on with whatever they already had.
 *
 * What that is depends on the bars before it, which is why it is not stated here: the bar
 * simply stops saying anything, and the nearest statement before it stands again.
 */
export function putDownInstrument(
  score: Score,
  partId: PartId,
  measureId: MeasureId,
): Command | string {
  const part = partOf(score, partId);
  if (part === null) return "there is no such part";
  const measure = part.measures[measureId];
  if (measure === undefined) return "that part has nothing for that bar";
  if (measure.instrument === null) return "that bar changes no instrument";
  return { kind: "setInstrument", partId, measureId, instrument: null };
}

/**
 * The part with every doubling taken off it.
 *
 * What a doubling cannot do is change what kind of staff the part is drawn as: a kit and a
 * tuning are the part's own for the whole piece. So a part rebuilt as a drum part or a tab
 * part cannot go on carrying instruments its player picked up while it was neither, and
 * they come off with the staff they were written on -- the refusal {@link takeUpInstrument}
 * makes, made where a part is rebuilt instead of where one is handed something.
 */
export function withoutDoublings(part: Part): Part {
  const measures: Record<MeasureId, PartMeasure> = {};
  for (const [id, measure] of Object.entries(part.measures)) {
    measures[id as MeasureId] =
      measure.instrument === null ? measure : { ...measure, instrument: null };
  }
  return { ...part, measures };
}

/** Everywhere a part picks another instrument up, in bar order. */
export function doublingsOf(score: Score, partId: PartId): readonly Doubling[] {
  const part = partOf(score, partId);
  if (part === null) return [];
  const found: Doubling[] = [];
  score.measures.forEach((measure, index) => {
    const instrument = part.measures[measure.id]?.instrument ?? null;
    if (instrument !== null) found.push({ index, measureId: measure.id, instrument });
  });
  return found;
}

/** What the player has in their hands at a bar, doublings and all. */
export function instrumentInHand(
  score: Score,
  partId: PartId,
  measureId: MeasureId,
): Instrument | null {
  const part = partOf(score, partId);
  const index = measureIndexOf(score, measureId);
  if (part === null || index < 0) return null;
  return instrumentAt(score, part, index);
}
